/**
 * Upload Queue Manager for Offline-First Inspection System
 *
 * Handles parallel upload of photos (5 concurrent) and signatures with retry logic
 */

import inspectionStorage from '../utils/inspection_storage.js';

class UploadQueue {
  constructor() {
    this.isProcessing = false;
    this.maxRetries = 3;
    this.baseDelay = 1000; // 1 second base delay for exponential backoff
    this.concurrencyLimit = 5; // Upload 5 photos at a time
  }

  /**
   * Process all pending uploads for an inspection
   *
   * Sync flow:
   * 1. Upload inspection responses (form data) to server → sets status to 'syncing'
   * 2. Upload photos to S3 (5 concurrent uploads at a time)
   * 3. Upload signatures to S3
   * 4. Notify server that uploads are complete → sets status to 'submitted'
   * 5. Clean up IndexedDB
   *
   * @param {string} inspectionUuid - Inspection UUID
   * @returns {Promise<{success: boolean, uploaded: number, failed: number}>}
   */
  async processInspection(inspectionUuid) {
    if (this.isProcessing) {
      return { success: false, uploaded: 0, failed: 0, message: 'Already processing' };
    }

    this.isProcessing = true;
    let photosUploaded = 0;
    let photosFailed = 0;
    let signaturesUploaded = 0;
    let signaturesFailed = 0;

    try {
      // Get inspection data from IndexedDB
      const inspection = await inspectionStorage.getInspection(inspectionUuid);
      if (!inspection) {
        console.error('Inspection not found in IndexedDB:', inspectionUuid);
        return { success: false, uploaded: 0, failed: 0, error: 'Inspection not found' };
      }

      // Step 1: Upload inspection responses (form data) to server first
      const responsesUploaded = await this.uploadInspectionResponses(inspectionUuid, inspection);
      if (!responsesUploaded) {
        console.error('Failed to upload inspection responses');
        return { success: false, uploaded: 0, failed: 0, error: 'Failed to upload responses' };
      }

      // Update inspection status to 'processing'
      inspection.status = 'processing';
      await inspectionStorage.saveInspection(inspectionUuid, inspection);

      // Step 2: Get all pending photos and signatures
      const photos = await inspectionStorage.getPhotos(inspectionUuid);
      const signatures = await inspectionStorage.getSignatures(inspectionUuid);

      const pendingPhotos = photos.filter(p => !p.uploaded);
      const pendingSignatures = signatures.filter(s => !s.uploaded);

      const totalItems = pendingPhotos.length + pendingSignatures.length;
      let completedItems = 0;

      // Emit initial progress with correct type based on what we're uploading first
      // Photos are uploaded first, so if there are photos, first item is a photo
      const firstType = pendingPhotos.length > 0 ? 'photo' : (pendingSignatures.length > 0 ? 'signature' : 'starting');
      this.emitProgress(totalItems, completedItems, firstType);

      // Step 3: Upload photos in parallel batches
      const photoResults = await this.uploadInBatches(
        pendingPhotos,
        async (photo) => {
          const success = await this.uploadPhoto(inspectionUuid, photo);
          if (success) {
            await inspectionStorage.markPhotoUploaded(photo.id);
          }
          completedItems++;
          this.emitProgress(totalItems, completedItems, 'photo');
          return success;
        }
      );

      // Count successes and failures
      photosUploaded = photoResults.filter(r => r.success).length;
      photosFailed = photoResults.filter(r => !r.success).length;

      // Step 4: Upload signatures
      for (const signature of pendingSignatures) {
        const success = await this.uploadSignature(inspectionUuid, signature);
        if (success) {
          signaturesUploaded++;
          await inspectionStorage.markSignatureUploaded(signature.id);
        } else {
          signaturesFailed++;
        }
        completedItems++;
        this.emitProgress(totalItems, completedItems, 'signature');
      }

      // Check if all uploads were successful
      const allSuccess = photosFailed === 0 && signaturesFailed === 0;

      // Step 5: Update inspection status and notify server
      if (allSuccess) {
        // All uploads complete - mark as ready_to_send
        if (inspection) {
          inspection.status = 'ready_to_send';
          await inspectionStorage.saveInspection(inspectionUuid, inspection);
        }

        // Notify server that uploads are complete
        await this.notifyUploadsComplete(inspectionUuid);

        // Emit completion event
        this.emitProgress(totalItems, completedItems, 'complete');
      } else {
        console.error(`Upload incomplete: ${photosFailed} photos and ${signaturesFailed} signatures failed`);
      }

      return {
        success: allSuccess,
        uploaded: photosUploaded + signaturesUploaded,
        failed: photosFailed + signaturesFailed
      };
    } catch (error) {
      console.error('Error processing upload queue:', error);
      return { success: false, uploaded: 0, failed: 0, error: error.message };
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Emit progress event for UI updates
   * @param {number} total - Total items to upload
   * @param {number} completed - Items completed so far
   * @param {string} type - Type of item being uploaded ('photo', 'signature', 'starting', 'complete')
   */
  emitProgress(total, completed, type) {
    window.dispatchEvent(new CustomEvent('upload-progress', {
      detail: { total, completed, type }
    }));
  }

  /**
   * Upload items in parallel batches with concurrency limit
   * @param {Array} items - Array of items to upload
   * @param {Function} uploadFn - Async function to process each item, returns boolean
   * @returns {Promise<Array>} - Array of results {item, success}
   */
  async uploadInBatches(items, uploadFn) {
    const results = [];

    // Process items in batches of concurrencyLimit
    for (let i = 0; i < items.length; i += this.concurrencyLimit) {
      const batch = items.slice(i, i + this.concurrencyLimit);

      // Upload all items in this batch in parallel
      const batchResults = await Promise.all(
        batch.map(async (item) => {
          try {
            const success = await uploadFn(item);
            return { item, success };
          } catch (error) {
            console.error('Error uploading item:', error);
            return { item, success: false };
          }
        })
      );

      results.push(...batchResults);
    }

    return results;
  }

  /**
   * Upload a single photo with retry logic
   * @param {string} inspectionUuid - Inspection UUID
   * @param {Object} photo - Photo object from IndexedDB
   * @returns {Promise<boolean>} - True if upload succeeded
   */
  async uploadPhoto(inspectionUuid, photo) {
    const { id, question_key, base64, order_position } = photo;

    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        // Convert base64 to blob for upload
        const blob = await this.base64ToBlob(base64);

        const formData = new FormData();
        formData.append('photo', blob, `photo_${id}.jpg`);
        formData.append('question_key', question_key);
        formData.append('order_position', order_position);

        const response = await fetch(`/inspections/${inspectionUuid}/photos`, {
          method: 'POST',
          body: formData,
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || ''
          }
        });

        if (response.ok) {
          return true;
        } else {
          const errorText = await response.text();
          console.error(`Upload failed (${response.status}):`, errorText);

          // Don't retry on client errors (4xx)
          if (response.status >= 400 && response.status < 500) {
            return false;
          }
        }
      } catch (error) {
        console.error(`Upload attempt ${attempt} failed:`, error);
      }

      // Wait before retrying (exponential backoff)
      if (attempt < this.maxRetries) {
        const delay = this.baseDelay * Math.pow(2, attempt - 1);
        await this.sleep(delay);
      }
    }

    console.error(`Photo ${id} failed after ${this.maxRetries} attempts`);
    return false;
  }

  /**
   * Upload a single signature with retry logic
   * @param {string} inspectionUuid - Inspection UUID
   * @param {Object} signature - Signature object from IndexedDB
   * @returns {Promise<boolean>} - True if upload succeeded
   */
  async uploadSignature(inspectionUuid, signature) {
    const { id, question_key, base64 } = signature;

    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        // Use base64 string directly (stored in IndexedDB for mobile compatibility)
        const response = await fetch(`/inspections/${inspectionUuid}/signatures`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || '',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            question_key: question_key,
            signature_data: base64
          })
        });

        if (response.ok) {
          return true;
        } else {
          const errorText = await response.text();
          console.error(`Upload failed (${response.status}):`, errorText);

          // Don't retry on client errors (4xx)
          if (response.status >= 400 && response.status < 500) {
            return false;
          }
        }
      } catch (error) {
        console.error(`Upload attempt ${attempt} failed:`, error);
      }

      // Wait before retrying (exponential backoff)
      if (attempt < this.maxRetries) {
        const delay = this.baseDelay * Math.pow(2, attempt - 1);
        await this.sleep(delay);
      }
    }

    console.error(`Signature ${id} failed after ${this.maxRetries} attempts`);
    return false;
  }

  /**
   * Upload inspection responses (form data) to server
   * Creates the inspection on server if it doesn't exist yet
   * @param {string} inspectionUuid - Inspection UUID
   * @param {Object} inspection - Inspection object from IndexedDB
   * @returns {Promise<boolean>} - True if upload succeeded
   */
  async uploadInspectionResponses(inspectionUuid, inspection) {
    try {
      // Create inspection on server with all data
      const response = await fetch('/inspections/sync', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          inspection: {
            uuid: inspectionUuid,
            property_id: inspection.property_id,
            responses: inspection.responses || {},
            status: 'syncing',  // Mark as 'syncing' until all uploads complete
            completed_at: inspection.completed_at
          }
        })
      });

      if (response.ok) {
        return true;
      } else {
        const errorText = await response.text();
        console.error(`Failed to create inspection (${response.status}):`, errorText);
        return false;
      }
    } catch (error) {
      console.error('Error uploading inspection responses:', error);
      return false;
    }
  }

  /**
   * Notify server that all uploads for an inspection are complete
   * @param {string} inspectionUuid - Inspection UUID
   */
  async notifyUploadsComplete(inspectionUuid) {
    try {
      const response = await fetch(`/inspections/${inspectionUuid}/complete_upload`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || '',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Clean up IndexedDB - delete the entire inspection including photos and signatures
        // This frees up storage space and removes it from the "Awaiting Upload" list
        await inspectionStorage.deleteInspection(inspectionUuid);
      } else {
        const errorText = await response.text();
        console.error('Failed to notify server of upload completion:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error notifying server:', error);
    }
  }

  /**
   * Convert blob to base64 data URL
   * @param {Blob} blob - Blob to convert
   * @returns {Promise<string>} - Base64 data URL
   */
  async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Convert base64 data URL to blob
   * @param {string} base64 - Base64 data URL (e.g., "data:image/jpeg;base64,...")
   * @returns {Promise<Blob>} - Blob object
   */
  async base64ToBlob(base64) {
    return new Promise((resolve, reject) => {
      try {
        // Extract the base64 data (remove the data URL prefix)
        const parts = base64.split(',');
        const contentType = parts[0].match(/:(.*?);/)[1];
        const raw = atob(parts[1]);

        // Convert to byte array
        const rawLength = raw.length;
        const byteArray = new Uint8Array(rawLength);
        for (let i = 0; i < rawLength; i++) {
          byteArray[i] = raw.charCodeAt(i);
        }

        // Create blob
        const blob = new Blob([byteArray], { type: contentType });
        resolve(blob);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Sleep for specified milliseconds
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise<void>}
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check connectivity and process any pending inspections
   * @returns {Promise<void>}
   */
  async processPendingInspections() {
    try {
      // Check if online
      if (!navigator.onLine) {
        return;
      }

      // Get all inspections with status 'submitted' or 'processing'
      const submittedInspections = await inspectionStorage.getInspectionsByStatus('submitted');
      const processingInspections = await inspectionStorage.getInspectionsByStatus('processing');

      const pendingInspections = [...submittedInspections, ...processingInspections];

      for (const inspection of pendingInspections) {
        await this.processInspection(inspection.uuid);
      }
    } catch (error) {
      console.error('Error processing pending inspections:', error);
    }
  }
}

// Export singleton instance
export default new UploadQueue();
